import { ArrowRight, Ban, PackageCheck, RotateCcw, ShoppingBag } from "lucide-react";
import { useRef } from "react";

import { cn } from "../lib/cn.js";
import { formatMoney } from "../lib/format.js";
import { type ReturnsView } from "../lib/reports.js";
import { Card } from "./ui/card.js";
import { InfoTooltip } from "./ui/info-tooltip.js";

/** The explanation behind the ⓘ — what "before" and "after" returns mean in this table. */
export const RETURNS_BRIDGE_INFO =
  "Last-click sales for these dates. Before returns counts every order the channels brought in, at its own value — " +
  "including orders cancelled or refunded since. After returns leaves out cancelled orders and takes each order's " +
  "refunds off that order. Both count each order on the day it was placed, so a later cancellation or refund " +
  "lowers the after-returns figure of the day the order was placed.";

const count = (value: number): string => value.toLocaleString("en-IN");
const plural = (value: number, one: string, many: string): string => (value === 1 ? one : many);
const roas = (revenue: number, spend: number): string => (spend > 0 ? `${(revenue / spend).toFixed(2)}x ROAS` : "No spend");

/**
 * BEFORE OR AFTER RETURNS, as a control (owner, 2026-10-30). A radio group with the look of the
 * tabs rail — it switches which last-click figures the table shows, it opens no panel.
 */
export function ReturnsViewToggle({
  value,
  onChange,
}: {
  readonly value: ReturnsView;
  readonly onChange: (view: ReturnsView) => void;
}): React.JSX.Element {
  const options: readonly { readonly value: ReturnsView; readonly label: string }[] = [
    { value: "original", label: "Before returns" },
    { value: "net", label: "After returns" },
  ];
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (from: number): void => {
    const next = (from + 1) % options.length;
    const option = options[next];
    if (option === undefined) return;
    onChange(option.value);
    refs.current[next]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label="Last-click sales"
      data-testid="returns-view-toggle"
      className="bg-muted text-muted-foreground inline-flex h-9 w-fit items-center rounded-lg p-[3px]"
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"].includes(event.key)) {
                event.preventDefault();
                move(index);
              }
            }}
            className={cn(
              "inline-flex h-[calc(100%-1px)] items-center rounded-md px-3 text-xs font-medium whitespace-nowrap transition-[color,box-shadow]",
              "focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
              selected ? "bg-background text-foreground shadow-sm" : "hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** The report's totals this strip reads — present whenever the API sent window-wide totals. */
export interface ReturnsTotals {
  readonly original_orders?: number;
  readonly original_revenue?: number;
  readonly cancelled_orders?: number;
  readonly cancelled_revenue?: number;
  readonly refunded_orders?: number;
  readonly refund_amount?: number;
  readonly order_count?: number;
  readonly total_sales?: number;
  readonly spend?: number;
}

/**
 * FROM WHAT THE CHANNELS SOLD TO WHAT THE STORE KEPT, in one line above Source Attribution: the
 * before-returns figure, what cancellations and refunds took, and the after-returns figure. The end
 * the table is showing is highlighted. The four always add up — the API's totals are exact sums.
 *
 * The toggle lives in the card's header, always: it stays put while the figures load, and keeps the
 * table's own toolbar on one line. Without the API's totals the figures are left out — never zeros.
 */
export function ReturnsBridge({
  totals,
  view,
  onViewChange,
}: {
  readonly totals: ReturnsTotals | null;
  readonly view: ReturnsView;
  readonly onViewChange: (view: ReturnsView) => void;
}): React.JSX.Element {
  const usable = totals !== null && totals.original_revenue !== undefined && totals.total_sales !== undefined;
  return (
    <section aria-label="Last-click sales before and after returns" data-testid="returns-bridge">
      <Card className="gap-0 p-0">
        <div className={cn("flex flex-wrap items-center justify-between gap-2 px-4 py-2", usable && "border-b")}>
          <span className="text-muted-foreground flex items-center gap-1 text-xs font-medium tracking-wide uppercase">
            Last-click sales, before and after returns
            <InfoTooltip text={RETURNS_BRIDGE_INFO} />
          </span>
          <ReturnsViewToggle value={view} onChange={onViewChange} />
        </div>
        {usable && <Steps totals={totals} view={view} />}
      </Card>
    </section>
  );
}

function Steps({ totals, view }: { readonly totals: ReturnsTotals; readonly view: ReturnsView }): React.JSX.Element {
  const n = (value: number | undefined): number => Number(value ?? 0);
  const spend = n(totals.spend);
  return (
    <div data-testid="returns-steps" className="grid grid-cols-1 items-stretch sm:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr]">
      <Step
        active={view === "original"}
        icon={<ShoppingBag className="size-4" />}
        label="Before returns"
        value={formatMoney(n(totals.original_revenue))}
        sub={`${count(n(totals.original_orders))} ${plural(n(totals.original_orders), "order", "orders")} · ${roas(n(totals.original_revenue), spend)}`}
      />
      <Arrow />
      <Step
        icon={<Ban className="size-4" />}
        label="Cancelled"
        value={`− ${formatMoney(n(totals.cancelled_revenue))}`}
        sub={`${count(n(totals.cancelled_orders))} ${plural(n(totals.cancelled_orders), "order", "orders")}`}
      />
      <Arrow />
      <Step
        icon={<RotateCcw className="size-4" />}
        label="Refunded"
        value={`− ${formatMoney(n(totals.refund_amount))}`}
        sub={`on ${count(n(totals.refunded_orders))} kept ${plural(n(totals.refunded_orders), "order", "orders")}`}
      />
      <Arrow />
      <Step
        active={view === "net"}
        icon={<PackageCheck className="size-4" />}
        label="After returns"
        value={formatMoney(n(totals.total_sales))}
        sub={`${count(n(totals.order_count))} ${plural(n(totals.order_count), "order", "orders")} · ${roas(n(totals.total_sales), spend)}`}
      />
    </div>
  );
}

function Arrow(): React.JSX.Element {
  return (
    <div aria-hidden className="text-muted-foreground/60 hidden items-center justify-center px-1 sm:flex">
      <ArrowRight className="size-4" />
    </div>
  );
}

function Step({
  icon,
  label,
  value,
  sub,
  active = false,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly active?: boolean;
}): React.JSX.Element {
  return (
    <div
      data-active={active || undefined}
      className={cn("flex flex-col gap-1 px-4 py-3", active && "bg-primary/5 ring-primary/30 ring-1 ring-inset sm:rounded-none")}
    >
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <span className={cn(active ? "text-primary" : "text-muted-foreground")}>{icon}</span>
        {label}
        {active && <span className="text-primary text-[10px] font-semibold tracking-wide uppercase">· in the table</span>}
      </div>
      <div className="text-foreground text-xl font-bold tracking-tight tabular-nums">{value}</div>
      <div className="text-muted-foreground text-[11px]">{sub}</div>
    </div>
  );
}
